import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Settings, X } from 'lucide-react';
import { FIXED_SECTIONS, SECTIONS, TOOL_SECTIONS, type NavSection } from '../../config/navigation';
import { itemsOn, sectionOn } from '../../config/navigationModules';
import { useMemberScope } from '../../hooks/useMemberScope';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useSwitchedOffModules } from '../../hooks/useSwitchedOffModules';

/** The Bureau first, then the tools in the order of the « Outils » menu (PLAN-BUREAU lot 3). */
const DRAWER_SECTIONS = [...FIXED_SECTIONS, ...TOOL_SECTIONS]
  .map((id) => SECTIONS.find((s) => s.id === id))
  .filter((s) => s !== undefined);
import { useActiveSection } from '../../hooks/useActiveSection';
import { AdminLogo } from '../ui/AdminLogo';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Raccourcis absents des sections (sur desktop ils vivent dans les quick links du header). */
const SHORTCUTS = [
  { name: 'Paramètres', href: '/admin/settings', icon: Settings },
];

/**
 * Drawer de navigation mobile (< lg). Toute la navigation en UN panneau :
 * les sections sont des accordéons dont les sous-items sont dépliés sur place
 * (section active pré-dépliée). Un sous-item est donc à 2 taps du hamburger,
 * là où l'ancien drawer imposait de naviguer vers la section puis de rouvrir
 * le menu pour voir ses sous-items.
 *
 * Off-canvas depuis la gauche + backdrop. Se ferme sur navigation, Escape, ou
 * clic backdrop. Verrouille le scroll du body tant qu'il est ouvert.
 */
export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const location = useLocation();
  const activeSection = useActiveSection();
  const [expanded, setExpanded] = useState<NavSection | null>(null);
  // A module switched off takes its sections and entries away (PLAN-GENERICITE § 3.7).
  const off = useSwitchedOffModules();
  const scope = useMemberScope();
  const canOpen = useCanOpen();

  // À chaque ouverture, déplie la section courante (et replie le reste).
  useEffect(() => {
    if (open) setExpanded(activeSection);
  }, [open, activeSection]);

  // Ferme le drawer dès qu'on change de route.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape pour fermer + lock du scroll body quand ouvert.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const isItemActive = (href: string) => {
    if (location.pathname === href) return true;
    if (href === '/admin') return false;
    return location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 lg:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel off-canvas */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[82vw] max-w-xs flex-col bg-white shadow-xl transition-transform duration-300 lg:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        {/* Header drawer */}
        <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-neutral-200 px-4">
          <Link to="/admin" className="flex items-center space-x-2.5">
            <AdminLogo size={28} />
            <span className="bg-gradient-to-r from-[#0066FF] to-[#00AAFF] bg-clip-text text-lg font-bold text-transparent">
              AutoQuote
            </span>
          </Link>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            aria-label="Fermer la navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4 pb-safe">
          <nav className="space-y-0.5">
            {DRAWER_SECTIONS.filter((section) => sectionOn(section.id, off, scope, canOpen)).map((section) => {
              const s = { ...section, sidebarItems: itemsOn(section.sidebarItems, off, scope, canOpen) };
              const isActive = activeSection === s.id;
              const Icon = s.icon;
              const hasChildren = s.sidebarItems.length > 0;
              const isExpanded = expanded === s.id;

              // Section feuille (Dashboard, Messagerie, Notes) : lien direct.
              if (!hasChildren) {
                return (
                  <Link
                    key={s.id}
                    to={s.href}
                    className={`group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-gradient-to-r from-[#0066FF] to-[#00AAFF] text-white shadow-md shadow-blue-500/20'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    }`}
                  >
                    <Icon
                      className={`mr-3 h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`}
                    />
                    <span className="truncate">{s.label}</span>
                  </Link>
                );
              }

              // Section accordéon : le tap déplie sur place, les sous-items naviguent.
              return (
                <div key={s.id}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : s.id)}
                    aria-expanded={isExpanded}
                    className={`flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-[#0066FF]'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    }`}
                  >
                    <Icon
                      className={`mr-3 h-4 w-4 flex-shrink-0 ${
                        isActive ? 'text-[#0066FF]' : 'text-neutral-400'
                      }`}
                    />
                    <span className="flex-1 truncate text-left">{s.label}</span>
                    <ChevronDown
                      className={`h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {isExpanded && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-neutral-100 pl-2">
                      {s.sidebarItems.map((item) => {
                        const itemActive = isItemActive(item.href);
                        const ItemIcon = item.icon;
                        return (
                          <React.Fragment key={item.href}>
                            {item.dividerBefore && <div className="my-2 border-t border-neutral-100" />}
                            <Link
                              to={item.href}
                              className={`group flex items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                                itemActive
                                  ? 'bg-blue-50 text-[#0066FF]'
                                  : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800'
                              }`}
                            >
                              <ItemIcon
                                className={`mr-3 h-4 w-4 flex-shrink-0 ${
                                  itemActive ? 'text-[#0066FF]' : 'text-neutral-400'
                                }`}
                              />
                              <span className="truncate">{item.name}</span>
                            </Link>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Raccourcis (desktop : quick links du header, invisibles < sm). */}
          <p className="mt-5 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
            Raccourcis
          </p>
          <nav className="space-y-0.5">
            {SHORTCUTS.filter((item) => canOpen(item.href)).map((item) => {
              const itemActive = isItemActive(item.href);
              const ItemIcon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    itemActive
                      ? 'bg-blue-50 text-[#0066FF]'
                      : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                  }`}
                >
                  <ItemIcon
                    className={`mr-3 h-4 w-4 flex-shrink-0 ${
                      itemActive ? 'text-[#0066FF]' : 'text-neutral-400'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}
